import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, RotateCw } from 'lucide-react';

export interface ImagemLightbox {
  url: string;
  legenda?: string | null;
}

interface Props {
  imagens: ImagemLightbox[];
  indexInicial?: number;
  onFechar: () => void;
}

export function LightboxImagem({ imagens, indexInicial = 0, onFechar }: Props) {
  const total = imagens.length;
  const [idx, setIdx] = useState(() => Math.max(0, Math.min(indexInicial, total - 1)));
  const escala = useRef(1);
  const pos = useRef({ x: 0, y: 0 });
  const rotacao = useRef(0);
  // Deitada (90/270) a imagem ocupa altura x largura trocadas e estoura a tela.
  // Este fator reencaixa a foto girada; em 0/180 vale sempre 1.
  const ajusteRot = useRef(1);
  const arrastando = useRef(false);
  const ultPos = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement | null>(null);

  const calcularAjuste = () => {
    if (rotacao.current % 180 === 0) { ajusteRot.current = 1; return; }
    const img = containerRef.current?.querySelector('[data-lb-img] img') as HTMLImageElement | null;
    if (!img?.offsetWidth || !img?.offsetHeight) { ajusteRot.current = 1; return; }
    ajusteRot.current = Math.min(1, (window.innerWidth * 0.9) / img.offsetHeight, (window.innerHeight * 0.85) / img.offsetWidth);
  };

  const aplicar = () => {
    const el = containerRef.current?.querySelector('[data-lb-img]') as HTMLElement | null;
    if (el) el.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px) rotate(${rotacao.current}deg) scale(${escala.current * ajusteRot.current})`;
    const pct = containerRef.current?.querySelector('[data-lb-pct]');
    if (pct) pct.textContent = Math.round(escala.current * 100) + '%';
  };
  const zoom = (delta: number) => { escala.current = Math.max(0.5, Math.min(5, escala.current + delta)); aplicar(); };
  // Girar preserva a rotacao escolhida (so recentraliza) — quem girou pra ler quer continuar lendo.
  const reset = () => { escala.current = 1; pos.current = { x: 0, y: 0 }; calcularAjuste(); aplicar(); };
  const girar = () => { rotacao.current = (rotacao.current + 90) % 360; pos.current = { x: 0, y: 0 }; escala.current = 1; calcularAjuste(); aplicar(); };

  // Reset zoom/pos/rotacao ao trocar de imagem
  useEffect(() => { rotacao.current = 0; reset(); }, [idx]);

  const proxima = () => setIdx((i) => (i + 1) % total);
  const anterior = () => setIdx((i) => (i - 1 + total) % total);

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopImmediatePropagation(); onFechar(); }
      else if (e.key === 'r' || e.key === 'R') { e.stopImmediatePropagation(); girar(); }
      else if (e.key === 'ArrowRight' && total > 1) { e.stopImmediatePropagation(); proxima(); }
      else if (e.key === 'ArrowLeft' && total > 1) { e.stopImmediatePropagation(); anterior(); }
    };
    document.addEventListener('keydown', k, true);
    return () => document.removeEventListener('keydown', k, true);
  }, [onFechar, total]);

  const atual = imagens[idx]!;

  return createPortal(
    <div ref={containerRef} className="fixed inset-0 z-[9999]" style={{ touchAction: 'none' }}
      onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/85" onClick={onFechar} />

      <div className="absolute top-4 right-4 flex gap-2 z-[10001]">
        <button onClick={(e) => { e.stopPropagation(); zoom(0.4); }} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white text-xl flex items-center justify-center select-none">+</button>
        <button onClick={(e) => { e.stopPropagation(); zoom(-0.4); }} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white text-xl flex items-center justify-center select-none">-</button>
        <button onClick={(e) => { e.stopPropagation(); reset(); }} className="px-3 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white text-xs flex items-center justify-center select-none">
          <span data-lb-pct>100%</span>
        </button>
        <button onClick={(e) => { e.stopPropagation(); girar(); }}
          className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center select-none"
          aria-label="Girar imagem" title="Girar 90° (R)">
          <RotateCw size={18} />
        </button>
        <a href={atual.url} download target="_blank" rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center">
          <Download size={18} />
        </a>
        <button onClick={(e) => { e.stopPropagation(); onFechar(); }} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white text-xl flex items-center justify-center select-none">x</button>
      </div>

      {total > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[10001] px-3 py-1.5 rounded-full bg-white/20 text-white text-sm select-none">
          {idx + 1} / {total}
        </div>
      )}

      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); anterior(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-[10001] w-12 h-12 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center"
            aria-label="Imagem anterior"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); proxima(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-[10001] w-12 h-12 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center"
            aria-label="Proxima imagem"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      <div className="absolute inset-0 flex items-center justify-center z-[10000] overflow-hidden"
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          arrastando.current = true;
          ultPos.current = { x: e.clientX, y: e.clientY };
          e.currentTarget.setPointerCapture(e.pointerId);
          (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
        }}
        onPointerMove={(e) => {
          if (!arrastando.current) return;
          const dx = e.clientX - ultPos.current.x;
          const dy = e.clientY - ultPos.current.y;
          pos.current = { x: pos.current.x + dx, y: pos.current.y + dy };
          ultPos.current = { x: e.clientX, y: e.clientY };
          aplicar();
        }}
        onPointerUp={(e) => { arrastando.current = false; (e.currentTarget as HTMLElement).style.cursor = escala.current > 1 ? 'grab' : 'zoom-in'; }}
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => { e.stopPropagation(); zoom(e.deltaY < 0 ? 0.3 : -0.3); }}
        onDoubleClick={(e) => { e.stopPropagation(); escala.current === 1 ? zoom(1) : reset(); }}
        style={{ cursor: 'zoom-in' }}
      >
        <div data-lb-img className="select-none" style={{ transition: 'transform 0.15s' }}>
          <img src={atual.url} alt="" className="max-w-[90vw] max-h-[85vh] object-contain rounded" draggable={false} />
        </div>
      </div>

      {atual.legenda && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[10001] max-w-[80vw] px-4 py-2 rounded-lg bg-black/60 text-white text-sm whitespace-pre-wrap text-center">
          {atual.legenda}
        </div>
      )}
    </div>,
    document.body,
  );
}
